import { useState } from 'react';
import { useI18n } from '../contexts/I18nContext';
import Layout from '../layout/Layout';
import portugalStepsData from '../locales/portugalsteps.json';

type Language = 'ar' | 'en';
type Level = 'bachelor' | 'master';

type LocalizedText = {
  ar?: string;
  en?: string;
};

type RequirementItem = {
  name: LocalizedText;
  details: LocalizedText;
};

type StepItem = {
  step: number;
  title: LocalizedText;
  description: LocalizedText;
};

const icons = {
  Check: () => (
    <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  Globe: () => (
    <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18"
      />
    </svg>
  ),
  Pin: () => (
    <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

export default function PortugalSteps() {
  const { language, setLanguage } = useI18n();
  const [selectedLevel, setSelectedLevel] = useState<Level>('bachelor');

  const lang = language as Language;
  const content = portugalStepsData.study_in_portugal;
  const levelData = content.levels[selectedLevel];

  const getText = (value?: LocalizedText) => {
    if (!value) return '';
    return value[lang] ?? value.ar ?? value.en ?? '';
  };

  const directionClass = language === 'ar' ? 'text-right' : 'text-left';

  const requirements = (levelData.requirements ?? []) as RequirementItem[];
  const residencyDocuments = (content.residency.documents ?? []) as RequirementItem[];
  const steps = (content.steps.items ?? []) as StepItem[];

  return (
    <Layout>
      <section className="relative overflow-hidden bg-[#f2f5fb] pb-10 pt-24 sm:pb-14 sm:pt-28 lg:pt-32">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#5B3FD0]/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#37A3C8]/20 blur-3xl" />

        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
          <article className="relative overflow-hidden rounded-3xl border border-[#d9e0ef] bg-white shadow-[0_16px_40px_rgba(18,37,74,0.08)]">
            <img
              src="https://images.unsplash.com/photo-1504593811423-6dd665756598?auto=format&fit=crop&w=1600&q=80"
              alt={language === 'ar' ? 'مشهد حضري في البرتغال' : 'City view in Portugal'}
              className="h-64 w-full object-cover sm:h-80"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3f]/85 via-[#0d1f3f]/45 to-transparent" />

            <div className={`absolute inset-x-0 bottom-0 p-4 sm:p-7 lg:p-8 ${directionClass}`}>
              <div className="mb-3 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center sm:gap-3">
                <p className="inline-flex w-fit rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs font-semibold tracking-[0.08em] text-white">
                  {getText(content.label)}
                </p>
                <button
                  onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/20"
                >
                  <span className="h-4 w-4">
                    <icons.Globe />
                  </span>
                  {language === 'ar' ? 'English' : 'العربية'}
                </button>
              </div>

              <h1 className="max-w-3xl text-xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">{getText(content.title)}</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-white/90 sm:mt-3 sm:text-base sm:leading-7">{getText(content.description)}</p>
            </div>
          </article>

          <article className="mt-5 rounded-3xl border border-[#dce4f1] bg-white p-5 shadow-sm sm:mt-7 sm:p-7">
            <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr] lg:items-center">
              <div className={directionClass}>
                <h2 className="text-xl font-bold text-[#12254a] sm:text-2xl">
                  {language === 'ar' ? 'متطلبات الدراسة' : 'Study Requirements'}
                </h2>
                <p className="mt-2 text-sm leading-7 text-[#52617f] sm:text-base">
                  {language === 'ar'
                    ? 'اختر المرحلة الدراسية لمعرفة المتطلبات الأساسية بشكل دقيق.'
                    : 'Select your degree level to view the exact requirement checklist.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#eef2fb] p-1.5">
                <button
                  onClick={() => setSelectedLevel('bachelor')}
                  className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition ${
                    selectedLevel === 'bachelor' ? 'bg-white text-[#12254a] shadow-sm' : 'text-[#5b6782]'
                  }`}
                >
                  {getText(content.levels.bachelor.title)}
                </button>
                <button
                  onClick={() => setSelectedLevel('master')}
                  className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition ${
                    selectedLevel === 'master' ? 'bg-white text-[#12254a] shadow-sm' : 'text-[#5b6782]'
                  }`}
                >
                  {getText(content.levels.master.title)}
                </button>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:mt-6 sm:gap-4">
              {requirements.map((item, index) => (
                <article key={`${getText(item.name)}-${index}`} className={`rounded-2xl border border-[#dde6f4] bg-[#f9fbff] p-4 sm:p-5 ${directionClass}`}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded-full bg-[#d6e7ff] p-1.5 text-[#294d88]">
                      <icons.Check />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-[#12254a] sm:text-lg">{getText(item.name)}</h3>
                      <p className="mt-1 text-sm leading-6 text-[#52617f] sm:text-base">{getText(item.details)}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {levelData.extra_requirement && (
              <div className={`mt-4 rounded-xl border border-[#e2d7ff] bg-[#f7f2ff] p-4 ${directionClass}`}>
                <p className="text-sm font-medium text-[#4b2e88] sm:text-base">{getText(levelData.extra_requirement)}</p>
              </div>
            )}
          </article>

          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <article className="overflow-hidden rounded-3xl border border-[#dce4f1] bg-white shadow-sm">
              <img
                src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1200&q=80"
                alt={language === 'ar' ? 'جامعة في البرتغال' : 'University in Portugal'}
                className="h-44 w-full object-cover"
                loading="lazy"
              />
              <div className={`p-5 sm:p-6 ${directionClass}`}>
                <h2 className="text-xl font-bold text-[#12254a] sm:text-2xl">{getText(content.residency.title)}</h2>
                <p className="mt-2 text-sm leading-7 text-[#52617f] sm:text-base">{getText(content.residency.description)}</p>

                <div className="mt-4 space-y-3">
                  {residencyDocuments.map((item, index) => (
                    <article key={`${getText(item.name)}-${index}`} className="rounded-xl border border-[#e1e8f4] bg-[#fcfdff] p-4">
                      <h3 className="text-sm font-semibold text-[#12254a] sm:text-base">{getText(item.name)}</h3>
                      <p className="mt-1 text-sm leading-6 text-[#52617f]">{getText(item.details)}</p>
                    </article>
                  ))}
                </div>
              </div>
            </article>

            <div className="space-y-5">
              <article className={`rounded-3xl border border-[#dce4f1] bg-white p-5 shadow-sm sm:p-6 ${directionClass}`}>
                <h2 className="text-xl font-bold text-[#12254a] sm:text-2xl">{getText(content.residency.vfs_title)}</h2>
                <p className="mt-2 text-sm leading-7 text-[#52617f] sm:text-base">{getText(content.residency.vfs_description)}</p>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {content.residency.locations.map((location, index) => (
                    <div key={`${location.ar}-${index}`} className="inline-flex w-full items-center gap-2 rounded-lg border border-[#dce4f1] bg-[#f8fafe] px-3 py-2.5 text-sm font-semibold text-[#213e72]">
                      <span className="h-4 w-4">
                        <icons.Pin />
                      </span>
                      {location[lang] ?? location.ar ?? location.en}
                    </div>
                  ))}
                </div>
              </article>

              <article className={`rounded-3xl border border-[#dce4f1] bg-white p-5 shadow-sm sm:p-6 ${directionClass}`}>
                <h2 className="text-xl font-bold text-[#12254a] sm:text-2xl">{getText(content.benefits.title)}</h2>
                <div className="mt-4 space-y-2.5">
                  {content.benefits.items.map((item, index) => (
                    <div key={`${item.ar}-${index}`} className="flex items-start gap-2">
                      <div className="mt-1 h-5 w-5 rounded-full bg-[#d7f0e2] p-1 text-[#2b6e4d]">
                        <icons.Check />
                      </div>
                      <p className="text-sm leading-6 text-[#475670] sm:text-base">{item[lang] ?? item.ar ?? item.en}</p>
                    </div>
                  ))}
                </div>
              </article>
            </div>
          </div>

          <article className={`mt-5 rounded-3xl border border-[#dce4f1] bg-white p-5 shadow-sm sm:mt-7 sm:p-7 ${directionClass}`}>
            <h2 className="text-xl font-bold text-[#12254a] sm:text-2xl">{getText(content.steps.title)}</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {steps.map((step) => (
                <article key={step.step} className="rounded-2xl border border-[#e1e8f4] bg-[#fcfdff] p-4 sm:p-5">
                  <p className="inline-flex rounded-full bg-[#e8f0ff] px-3 py-1 text-xs font-semibold text-[#2a4f8a] sm:text-sm">
                    {language === 'ar' ? `الخطوة ${step.step}` : `Step ${step.step}`}
                  </p>
                  <h3 className="mt-2 text-base font-semibold text-[#12254a] sm:text-lg">{getText(step.title)}</h3>
                  <p className="mt-1 text-sm leading-6 text-[#52617f] sm:text-base">{getText(step.description)}</p>
                </article>
              ))}
            </div>
          </article>

          <article className={`mt-5 rounded-3xl border border-[#d7dff0] bg-gradient-to-br from-[#172f60] to-[#1b5f7e] p-6 shadow-[0_16px_30px_rgba(17,41,79,0.24)] sm:mt-7 sm:p-8 ${directionClass}`}>
            <h2 className="text-xl font-bold text-white sm:text-3xl">{getText(content.cta.title)}</h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-white/90 sm:text-base">{getText(content.cta.description)}</p>
          </article>
        </div>
      </section>
    </Layout>
  );
}
